// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type OptionId = string | number;

/**
 * Tracks option IDs that are one step away from quantity limits.
 * - oneToZero: Options with qty === 1 (one decrement away from zero)
 * - oneToMax: Options with qty === limit - 1 (one increment away from max)
 */
export interface QuantityLimitIds {
  oneToZero: OptionId[];
  oneToMax: OptionId[];
}

/**
 * Context for determining if selection should be cleared.
 */
export interface SelectionClearContext {
  selectedOptionId: OptionId | undefined;
  prevQtyLimitIds: QuantityLimitIds;
  isSelectedOptionAtMaxQty: boolean;
  isSelectedOptionAtZeroQty: boolean;
  isRemoving: boolean;
}

// ============================================================================
// QUANTITY LIMIT CALCULATIONS
// ============================================================================

/**
 * Calculates which options are one step away from reaching quantity limits.
 * - oneToMax: Options at (countersLimit - 1), one increment from max
 * - oneToZero: Options at qty 1, one decrement from zero
 */
export const calculateQuantityLimitIds = (
  optionsCounters: Record<OptionId, number>,
  countersLimit: number
): QuantityLimitIds => {
  const oneToMax: OptionId[] = [];
  const oneToZero: OptionId[] = [];

  Object.entries(optionsCounters).forEach(([id, qty]) => {
    if (qty === countersLimit - 1) {
      oneToMax.push(id);
    }
    if (qty === 1) {
      oneToZero.push(id);
    }
  });

  return { oneToMax, oneToZero };
};

// ============================================================================
// ARRAY COMPARISON
// ============================================================================

/**
 * Compares two QuantityLimitIds objects for equality.
 * Uses Set-based comparison to avoid string conversion antipattern.
 */
export const areQuantityLimitIdsEqual = (
  a: QuantityLimitIds,
  b: QuantityLimitIds
): boolean => {
  const aIds = [...a.oneToZero, ...a.oneToMax];
  const bIds = [...b.oneToZero, ...b.oneToMax];

  if (aIds.length !== bIds.length) {
    return false;
  }

  const aSet = new Set(aIds.map(String));

  return bIds.every((id) => aSet.has(String(id)));
};

// ============================================================================
// SELECTION CLEAR PREDICATES
// ============================================================================

/**
 * Checks if the selected option just transitioned to max quantity.
 */
export const hasSelectedOptionReachedMax = (
  selectedOptionId: OptionId | undefined,
  prevQtyLimitIds: QuantityLimitIds,
  isSelectedOptionAtMaxQty: boolean
): boolean =>
  prevQtyLimitIds.oneToMax.includes(selectedOptionId ?? -1) &&
  isSelectedOptionAtMaxQty;

/**
 * Checks if the selected option just transitioned to zero quantity.
 */
export const hasSelectedOptionReachedZero = (
  selectedOptionId: OptionId | undefined,
  prevQtyLimitIds: QuantityLimitIds,
  isSelectedOptionAtZeroQty: boolean
): boolean =>
  prevQtyLimitIds.oneToZero.includes(selectedOptionId ?? -1) &&
  isSelectedOptionAtZeroQty;

/**
 * Determines if selection circles should be cleared based on quantity limit transitions.
 *
 * Selection is cleared when:
 * 1. Selected option reached zero (decremented from 1 to 0)
 * 2. Selected option reached max (incremented to limit)
 * 3. Selected option is at max and we're not in removing mode
 */
export const shouldClearSelection = ({
  selectedOptionId,
  prevQtyLimitIds,
  isSelectedOptionAtMaxQty,
  isSelectedOptionAtZeroQty,
  isRemoving,
}: SelectionClearContext): boolean => {
  const reachedZero = hasSelectedOptionReachedZero(
    selectedOptionId,
    prevQtyLimitIds,
    isSelectedOptionAtZeroQty
  );

  const reachedMax = hasSelectedOptionReachedMax(
    selectedOptionId,
    prevQtyLimitIds,
    isSelectedOptionAtMaxQty
  );

  const atMaxNotRemoving = isSelectedOptionAtMaxQty && !isRemoving;

  return reachedZero || reachedMax || atMaxNotRemoving;
};

// ============================================================================
// REMOVAL PREDICATES
// ============================================================================

/**
 * Checks if removal should be prevented for the given option.
 * Removal is prevented when:
 * 1. The option is the default option (cannot remove default)
 * 2. The option is selected AND at zero quantity (nothing to remove)
 */
export const shouldPreventRemoval = (
  optionId: OptionId,
  defaultOptionId: OptionId | undefined,
  isSelectedOption: boolean,
  isSelectedOptionAtZeroQty: boolean
): boolean =>
  optionId === defaultOptionId ||
  (isSelectedOption && isSelectedOptionAtZeroQty);
